/**
 * 导航（总纲 §2.2、§7-1）：左侧 **4 项 + 一个齿轮**，没有分组标题。
 *
 *   今天 · 员工 · 客户 · 费用 · ⚙ 设置
 *
 * 权限不够的项**不渲染**，不灰置（Skill shell.md）。一个入口下面挂着几页时，
 * 任一子页看得到就渲染入口（`caps` 任一命中），进去之后子页各自再按 cap 收窄。
 */
import type { ComponentType } from 'react';
import {
  ApiOutlined,
  AuditOutlined,
  ContactsOutlined,
  DownloadOutlined,
  FileProtectOutlined,
  HomeOutlined,
  IdcardOutlined,
  MessageOutlined,
  PhoneOutlined,
  ScheduleOutlined,
  SettingOutlined,
  TeamOutlined,
  UsergroupAddOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { canAny, type Capability } from '@/lib/permission';
import { CONSOLE_HOME } from '@/lib/paths';

export interface NavItem {
  to: string;
  label: string;
  icon: ComponentType;
  /** 任一命中即渲染 */
  caps: readonly Capability[];
  /** 精确匹配（今天），默认前缀匹配 */
  exact?: boolean;
}

/** 左侧 4 项。 */
export const NAV_MAIN: readonly NavItem[] = [
  { to: CONSOLE_HOME, label: '今天', icon: HomeOutlined, caps: ['employee:read'], exact: true },
  { to: '/employees', label: '员工', icon: TeamOutlined, caps: ['employee:read'] },
  { to: '/customers', label: '客户', icon: ContactsOutlined, caps: ['lead:read', 'call:read'] },
  { to: '/billing', label: '费用', icon: WalletOutlined, caps: ['finance:read'] },
];

/**
 * 设置页里的子项（齿轮点开之后），顺序就是页内左栏顺序：总纲 §2.2 的六个入口。
 * 「电话线路 / 短信 / 加微」是一个入口，进去后由 `RESOURCE_TABS` 切三页。
 */
export const SETTINGS_ITEMS: readonly NavItem[] = [
  { to: '/settings/company', label: '企业认证', icon: IdcardOutlined, caps: ['tenant:read'] },
  { to: '/settings/members', label: '团队成员', icon: AuditOutlined, caps: ['member:read'] },
  {
    to: '/settings/resources',
    label: '电话线路 / 短信 / 加微',
    icon: PhoneOutlined,
    caps: ['resource:read'],
  },
  { to: '/settings/developer', label: '开发者', icon: ApiOutlined, caps: ['apikey:write'] },
  {
    to: '/settings/exports',
    label: '导出记录',
    icon: DownloadOutlined,
    caps: ['export:write'],
  },
  { to: '/settings/logs', label: '操作日志', icon: ScheduleOutlined, caps: ['audit:read'] },
];

/** 设置 · 电话线路 / 短信 / 加微 页顶部三个子面。 */
export const RESOURCE_TABS: readonly NavItem[] = [
  {
    to: '/settings/resources/lines',
    label: '电话线路',
    icon: PhoneOutlined,
    caps: ['resource:read'],
  },
  {
    to: '/settings/resources/sms',
    label: '短信',
    icon: MessageOutlined,
    caps: ['resource:read'],
  },
  {
    to: '/settings/resources/wechat',
    label: '加微',
    icon: UsergroupAddOutlined,
    caps: ['resource:read'],
  },
];

/** 齿轮本身：任一子项看得到就有齿轮。 */
export const NAV_SETTINGS: NavItem = {
  to: '/settings',
  label: '设置',
  icon: SettingOutlined,
  caps: Array.from(new Set(SETTINGS_ITEMS.flatMap((i) => i.caps))),
};

/** 客户页顶部三个子面。 */
export const CUSTOMER_TABS: readonly NavItem[] = [
  { to: '/customers/leads', label: '名单', icon: ContactsOutlined, caps: ['lead:read'] },
  { to: '/customers/calls', label: '通话记录', icon: PhoneOutlined, caps: ['call:read'] },
  {
    to: '/customers/intents',
    label: '意向客户',
    icon: UsergroupAddOutlined,
    caps: ['call:read'],
  },
];

/** 费用页顶部四个子面。 */
export const BILLING_TABS: readonly NavItem[] = [
  { to: '/billing', label: '本月', icon: WalletOutlined, caps: ['finance:read'], exact: true },
  { to: '/billing/seats', label: '坐席', icon: IdcardOutlined, caps: ['finance:read'] },
  { to: '/billing/bills', label: '账单', icon: ScheduleOutlined, caps: ['finance:read'] },
  {
    to: '/billing/invoices',
    label: '发票与合同',
    icon: FileProtectOutlined,
    caps: ['finance:read'],
  },
];

export function visibleItems(
  items: readonly NavItem[],
  roles: readonly string[] | undefined,
): NavItem[] {
  return items.filter((i) => canAny(roles, [...i.caps]));
}

/** 左侧主导航（4 项里看得到的）。 */
export const visibleMain = (roles: readonly string[] | undefined) =>
  visibleItems(NAV_MAIN, roles);

/** 齿轮要不要画。 */
export const showSettings = (roles: readonly string[] | undefined) =>
  canAny(roles, [...NAV_SETTINGS.caps]);

/** 进了 /settings 之后，第一个看得到的子页。 */
export function firstSettingsPath(roles: readonly string[] | undefined): string | null {
  return visibleItems(SETTINGS_ITEMS, roles)[0]?.to ?? null;
}

/** 进了 /customers 之后，第一个看得到的子面。 */
export function firstCustomerPath(roles: readonly string[] | undefined): string | null {
  return visibleItems(CUSTOMER_TABS, roles)[0]?.to ?? null;
}
